import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Pencil, Trash2, Unlink, Coffee, CheckCircle, ScrollText } from 'lucide-react';
import { API_BASE_URL, cn, getStatusBg, getStatusColor } from '../../lib/utils';
import { useAppSelector } from '../../store';
import BreakTimerDisplay from './BreakTimerDisplay';
import ResourceAuditLogPanel from './ResourceAuditLogPanel';
import type { Resource, ResourceType, ResourceStatus } from '../../types';

const typeOptions: ResourceType[] = ['vehicle', 'equipment', 'personnel', 'facility'];
const statusOptions: ResourceStatus[] = [
  'available',
  'dispatched',
  'deployed',
  'en_route',
  'on_break',
  'maintenance',
  'offline',
  'reserved',
];
const departmentOptions = ['Fire', 'Police', 'EMS', 'Hazmat', 'Search & Rescue', 'Other'];

type ResourceTab = 'resource' | 'audit';

const resourceTabs: { id: ResourceTab; label: string; icon: React.ElementType }[] = [
  { id: 'resource', label: 'Resource', icon: Pencil },
  { id: 'audit', label: 'Audit Log', icon: ScrollText },
];

interface EditResourceModalProps {
  open: boolean;
  onClose: () => void;
  resource: Resource | null;
  onSaved?: (resource: Resource) => void;
  onDeleted?: (resourceId: string) => void;
}

export default function EditResourceModal({ open, onClose, resource, onSaved, onDeleted }: EditResourceModalProps) {
  const { token } = useAppSelector((state) => state.auth);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [applyingStatus, setApplyingStatus] = useState(false);
  const [endingBreak, setEndingBreak] = useState(false);
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);
  const [liveResource, setLiveResource] = useState<Resource | null>(null);
  const [clearAssignment, setClearAssignment] = useState(true);
  const [activeTab, setActiveTab] = useState<ResourceTab>('resource');

  const [name, setName] = useState('');
  const [type, setType] = useState<ResourceType>('vehicle');
  const [status, setStatus] = useState<ResourceStatus>('available');
  const [department, setDepartment] = useState('Fire');
  const [capabilities, setCapabilities] = useState('');
  const [assignedPersonnel, setAssignedPersonnel] = useState('');
  const [notes, setNotes] = useState('');

  const refreshLiveResource = useCallback(async () => {
    if (!resource || !token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/resources/${resource.id}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.resource) {
          setLiveResource(data.resource);
          setStatus(data.resource.status);
        }
      }
    } catch {
      /* ignore */
    }
  }, [resource, token]);

  useEffect(() => {
    if (!resource) return;
    setName(resource.name || '');
    setType(resource.type || 'vehicle');
    setStatus(resource.status || 'available');
    setDepartment(resource.department || 'Fire');
    setCapabilities((resource.capabilities || []).join(', '));
    setAssignedPersonnel((resource.assignedPersonnel || []).join(', '));
    setNotes(resource.notes || '');
    setConfirmDelete(false);
    setLiveResource(resource);
    setAuditRefreshKey((k) => k + 1);
    setActiveTab('resource');
  }, [resource]);

  if (!open || !resource) return null;

  const active = liveResource || resource;

  const bumpAudit = () => {
    setAuditRefreshKey((k) => k + 1);
    refreshLiveResource();
    onSaved?.(active);
  };

  const handleEndBreak = async () => {
    const breakId = active.activeBreak?.requestId;
    if (!breakId || !token) return;
    setEndingBreak(true);
    try {
      const res = await fetch(`${API_BASE_URL}/break-requests/${breakId}/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) bumpAudit();
    } finally {
      setEndingBreak(false);
    }
  };

  const handleApplyStatus = async () => {
    if (!token) return;
    setApplyingStatus(true);
    try {
      if (active.activeBreak && status !== 'on_break') {
        const breakId = active.activeBreak.requestId;
        if (breakId) {
          await fetch(`${API_BASE_URL}/break-requests/${breakId}/end`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      }

      const body: Record<string, unknown> = { status };
      if (clearAssignment && (status === 'available' || status === 'maintenance' || status === 'offline')) {
        body.assignedIncident = null;
      }

      const res = await fetch(`${API_BASE_URL}/resources/${resource.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setLiveResource(data.resource);
        bumpAudit();
      }
    } finally {
      setApplyingStatus(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const capsList = capabilities.split(',').map((c) => c.trim()).filter(Boolean);
      const personnelList = assignedPersonnel.split(',').map((p) => p.trim()).filter(Boolean);

      const res = await fetch(`${API_BASE_URL}/resources/${resource.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: name.trim(),
          type,
          department,
          capabilities: capsList,
          assignedPersonnel: personnelList,
          notes: notes.trim() || undefined,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setLiveResource(data.resource);
        bumpAudit();
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/resources/${resource.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        onDeleted?.(resource.id);
        onClose();
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDisconnectMDT = async () => {
    setDisconnecting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/resources/${resource.id}/disconnect-mdt`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventId: active.eventId,
          reason: 'Disconnected by Command operator',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setLiveResource(data.resource);
        bumpAudit();
      }
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-card border border-border rounded-xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-foreground flex items-center gap-2">
            <Pencil className="w-4 h-4 text-primary" />
            {active.name}
            <span className="text-muted-foreground font-mono text-xs font-normal">({active.id})</span>
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-accent rounded-lg transition-colors">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex items-center gap-1 px-5 border-b border-border flex-shrink-0">
          {resourceTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-3 py-2.5 text-xs font-semibold transition-all relative flex items-center gap-1.5',
                  activeTab === tab.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-primary rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {activeTab === 'resource' ? (
            <div className="space-y-4">
          {/* Operations */}
          <div className="rounded-lg border border-border bg-accent/20 p-3 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Status &amp; breaks</p>
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium border', getStatusBg(active.status), getStatusColor(active.status))}>
                {active.status.replace('_', ' ')}
              </span>
              {active.assignedIncident && (
                <span className="text-[10px] text-primary font-mono">Assigned: {active.assignedIncident}</span>
              )}
            </div>

            {active.pendingBreakRequest && (
              <div className="px-2 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/25">
                <p className="text-[10px] font-bold text-purple-300">
                  Break requested — {active.pendingBreakRequest.typeLabel}
                </p>
                {active.pendingBreakRequest.notes && (
                  <p className="text-[9px] text-muted-foreground mt-0.5 italic">{active.pendingBreakRequest.notes}</p>
                )}
                <p className="text-[9px] text-muted-foreground mt-0.5">Respond via the break alert panel</p>
              </div>
            )}

            {active.activeBreak && (
              <div className="px-2 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-[10px] text-purple-300">
                    <Coffee className="w-3.5 h-3.5" />
                    On break — {active.activeBreak.typeLabel}
                    <BreakTimerDisplay activeBreak={active.activeBreak} compact />
                  </div>
                  <button
                    type="button"
                    onClick={handleEndBreak}
                    disabled={endingBreak}
                    className="px-2 py-1 rounded-lg border border-purple-500/30 text-purple-300 text-[10px] font-semibold hover:bg-purple-500/10 flex items-center gap-1 disabled:opacity-50"
                  >
                    {endingBreak ? <Loader2 className="w-3 h-3 animate-spin" /> : <Coffee className="w-3 h-3" />}
                    End Break
                  </button>
                </div>
                {active.activeBreak.commandNotes && (
                  <p className="text-[9px] text-muted-foreground">{active.activeBreak.commandNotes}</p>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[140px]">
                <label className="text-[10px] font-medium text-muted-foreground block mb-1">Change status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ResourceStatus)}
                  className="w-full px-2 py-1.5 rounded-lg bg-background border border-border text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>{s.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={handleApplyStatus}
                disabled={applyingStatus}
                className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 flex items-center gap-1.5 disabled:opacity-50"
              >
                {applyingStatus ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                Apply Status
              </button>
            </div>
            {(status === 'available' || status === 'maintenance' || status === 'offline') && active.assignedIncident && (
              <label className="flex items-center gap-2 text-[10px] text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={clearAssignment}
                  onChange={(e) => setClearAssignment(e.target.checked)}
                  className="rounded border-border"
                />
                Clear current incident assignment
              </label>
            )}
          </div>

          {/* Resource details */}
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Resource details</p>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Call Sign / Name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="e.g. MD01, Engine-7"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-foreground block mb-1">Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as ResourceType)}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {typeOptions.map((t) => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-foreground block mb-1">Department</label>
                <select
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {departmentOptions.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Assigned Personnel</label>
              <input
                value={assignedPersonnel}
                onChange={(e) => setAssignedPersonnel(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="Comma separated, e.g. Rachel Brooks, John Smith"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Capabilities</label>
              <input
                value={capabilities}
                onChange={(e) => setCapabilities(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="Comma separated, e.g. pumper, rescue, aerial"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                placeholder="Optional notes..."
              />
            </div>
            {active.inUseBy && (
              <div className="flex items-center gap-2 p-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                <span className="text-xs text-orange-400 font-medium">MDT Connected</span>
                <button
                  type="button"
                  onClick={handleDisconnectMDT}
                  disabled={disconnecting}
                  className="ml-auto px-2 py-1 rounded bg-orange-500 text-white text-xs font-medium hover:bg-orange-600 transition-colors flex items-center gap-1 disabled:opacity-50"
                >
                  {disconnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />}
                  Disconnect MDT
                </button>
              </div>
            )}
          </form>
            </div>
          ) : (
            <ResourceAuditLogPanel
              resource={active}
              refreshKey={auditRefreshKey}
              fullHeight
            />
          )}
        </div>

        <div className="flex gap-2 px-5 py-4 border-t border-border flex-shrink-0">
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors flex items-center gap-1.5 ${
              confirmDelete
                ? 'bg-red-500 text-white border-red-500 hover:bg-red-600'
                : 'border-red-500/30 text-red-400 hover:bg-red-500/10'
            }`}
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            {confirmDelete ? 'Confirm Delete' : 'Delete'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-accent transition-colors"
          >
            Close
          </button>
          {activeTab === 'resource' && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? 'Saving...' : 'Save Details'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
